import OrganizationSelect from 'Components/Generic/BOSFilterSelect/OrganizationSelect';
import ResourceSelect from 'Components/Generic/BOSSelect/ResourceSelect';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import OrgNameWithDrawerButton from 'Components/SiteDrawerContainer/OrgNameWithDrawerButton';
import ResourceDrawerButton from 'Components/SiteDrawerContainer/ResourceDrawerButton';
import { useAlertContext } from 'Data/context/Alerts';
import { getOrgNameFromId } from 'Data/context/Organizations';
import ServiceContractDetailsData from 'Data/ServiceContractDetailsData';
import ServiceContractDetailsDocumentData from 'Data/ServiceContractDetailsDocumentData';
import DataManager from 'Lib/DataManager';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import { useValidation } from 'Lib/hooks/useValidation';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import ServiceContractDetailsDocumentsPanel from './ServiceContractDetailsDocumentsPanel';
import styles from './ServiceContracts.module.css';

let DM = new DataManager();

const ServiceContractDetailsInformation = ({
  serviceContractDetailsId,
  serviceContractDetailsObject,
  serviceContractId,
  editable = false,
  parentEditState = false,
  onCancel = () => {},
}) => {
  const {
    currentObject: serviceContractDetails,
    hasBeenUpdated,
    reset: resetServiceContractDetails,
    updateField,
    originalObject: originalServiceContractDetails,
    doSave,
  } = useUpdatableObject(
    (recordData) =>
      ServiceContractDetailsData.updateRecord(
        recordData,
        `RECORD_${recordData.id}_UPDATED`,
      ),
    ServiceContractDetailsData.createRecord,
  );

  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalServiceContractDetails,
    undoFunction: ServiceContractDetailsData.updateRecord,
    onUndo: (originalServiceContractDetails) =>
      fetchServiceContractDetails(originalServiceContractDetails.id),
    objectType: 'Resource Details',
  });

  const { openDeleteDialog } = useAlertContext();

  let isNew = !serviceContractDetails?.id;

  useEffect(() => {
    DM.add(
      ServiceContractDetailsData.registerListener(
        'BLANK_RECORD_LOADED',
        onServiceContractDetailsLoaded,
      ),
      ServiceContractDetailsData,
    );
    DM.add(
      ServiceContractDetailsData.registerListener(
        `RECORD_${
          serviceContractDetailsId || serviceContractDetailsObject?.id || 'NEW'
        }_LOADED`,
        onServiceContractDetailsLoaded,
      ),
      ServiceContractDetailsData,
    );
    DM.add(
      ServiceContractDetailsData.registerListener(
        `RECORD_${
          serviceContractDetailsId || serviceContractDetailsObject?.id || 'NEW'
        }_UPDATED`,
        onServiceContractDetailsUpdated,
      ),
      ServiceContractDetailsData,
    );
    DM.add(
      ServiceContractDetailsDocumentData.registerListener(
        'RECORDS_DELETED',
        onDocumentDeleted,
      ),
      ServiceContractDetailsDocumentData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  let fields = {
    organization_id: {
      label: 'Core Facility',
      isRequired: true,
      isNumber: true,
      value: {
        render: (inEditState, blurField) => {
          return inEditState ? (
            <OrganizationSelect
              isRequired
              defaultSelectedId={serviceContractDetails.organization_id}
              onSelectOrganization={(org) =>
                updateField('organization_id', org?.id || null)
              }
              onBlur={blurField}
            />
          ) : (
            <OrgNameWithDrawerButton
              orgId={serviceContractDetails.organization_id}
            />
          );
        },
      },
    },

    resource_id: {
      label: 'Resource Name',
      isRequired: false,
      isNumber: true,
      // fieldSchema: joi
      //   .number()
      // .required()
      // .messages({ 'any.required': 'Resource / Asset is required' }),
      value: {
        render: (inEditState, blurField) => {
          return inEditState ? (
            <ResourceSelect
              orgId={serviceContractDetails.organization_id}
              defaultSelectedId={serviceContractDetails.resource_id}
              onSelectResource={(newResource) =>
                updateField('resource_id', newResource?.id || null)
              }
              onBlur={blurField}
              readOnly={!serviceContractDetails?.organization_id}
              inactive={!serviceContractDetails?.organization_id}
            />
          ) : (
            <ResourceDrawerButton
              inEditState={false}
              drawerTitle={serviceContractDetails.resource?.name}
              label={serviceContractDetails.resource?.name}
              resourceId={serviceContractDetails.resource_id}
            />
          );
        },
      },
    },
  };

  let { resetValidation, getValidatedDetailTable, ValidatedSaveButton } =
    useValidation(fields, serviceContractDetails);

  useEffect(() => {
    if (!serviceContractDetailsId && !serviceContractDetailsObject?.id) {
      serviceContractId &&
        serviceContractDetails?.service_contract_id !== serviceContractId &&
        onServiceContractDetailsLoaded({});
    }
  }, [serviceContractId, serviceContractDetails]);

  useEffect(() => {
    if (serviceContractDetailsId && serviceContractDetailsId >= 0) {
      fetchServiceContractDetails(serviceContractDetailsId);
    }
  }, [serviceContractDetailsId]);

  useEffect(() => {
    if (!editable) {
      if (serviceContractDetailsObject?.id) {
        resetServiceContractDetails(serviceContractDetailsObject);
        resetUndo(() =>
          fetchServiceContractDetails(serviceContractDetailsObject.id),
        );
      }
    } else if (
      serviceContractDetailsObject?.id &&
      Number(serviceContractDetailsObject.id) >= 0
    ) {
      fetchServiceContractDetails(serviceContractDetailsObject.id);
    }
  }, [serviceContractDetailsObject]);

  const onServiceContractDetailsLoaded = (payload) => {
    let matchingId =
      serviceContractDetailsId ||
      serviceContractDetailsObject?.id ||
      payload?.id;

    let scdObject = { ...payload, service_contract_id: serviceContractId };

    if (payload?.id === matchingId) {
      resetServiceContractDetails(scdObject);
      resetUndo(() => fetchServiceContractDetails(scdObject.id));
      resetValidation();
    }
  };

  const refreshServiceContractDetails = () => {
    let scdId = serviceContractDetailsId || serviceContractDetailsObject?.id;
    if (scdId) {
      fetchServiceContractDetails(scdId);
    }
  };
  const fetchServiceContractDetails = (id) => {
    ServiceContractDetailsData.getServiceContractDetailsAndAssociations(
      id,
      `RECORD_${id}_LOADED`,
    );
  };

  const onServiceContractDetailsUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
    }
  };

  const onDocumentDeleted = (payload) => {
    let scdId = serviceContractDetailsId || serviceContractDetails?.id;
    if (scdId && payload?.whereClause?.service_contract_details_id === scdId) {
      fetchServiceContractDetails(scdId);
    }
  };
  const undoAction = () => {
    fetchServiceContractDetails(serviceContractDetails.id);
    resetValidation();
  };

  const cancelButton = isNew && (
    <CancelButton fill={false} onCancel={onCancel} />
  );

  const undoButton = editable && !isNew && hasBeenUpdated && (
    <UndoButton clickHandler={undoAction} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <ValidatedSaveButton doSave={doSave} />
  );

  const infoFooter = (
    <ButtonFooter>
      {cancelButton}
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  const onDeleteClicked = () => {
    if (serviceContractDetails.documents.length) {
      openDeleteDialog({
        dialogTitle: 'Unlink Core/Asset from Service Contract?',
        dialogMessage: (
          <div style={{ padding: 10 }}>
            This will remove the association the service contract has to:
            <ul style={{ listStyle: 'inside' }}>
              <li>The core facility,</li>
              <li>The asset, and</li>
              <li>{`The ${serviceContractDetails.documents.length} documents uploaded`}</li>
            </ul>
          </div>
        ),
        confirmText: 'Unlink Core/Asset',
        onConfirmDelete: removeResourceFromSC,
      });
    } else {
      removeResourceFromSC();
    }
  };

  const removeResourceFromSC = () => {
    serviceContractDetails?.id &&
      ServiceContractDetailsData.deleteRecords([serviceContractDetails.id]);
  };

  let unlinkButton = serviceContractDetails?.id && (
    <DeleteButton
      miniVersion
      useBanIcon
      tooltipText={'Unlink from Service Contract'}
      onDeleteItem={onDeleteClicked}
    />
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
        paddingLeft: 20,
      }}>
      {isNew || editable || parentEditState ? (
        <>
          <div className={styles.resource_box}>
            {getValidatedDetailTable(true)}
            {infoFooter}
          </div>
          {unlinkButton}
        </>
      ) : (
        <>
          <div className={styles.resource_box}>
            <div className={styles.label_and_value_box}>
              <span className={styles.resource_label}>{'Core Facility: '}</span>
              {getOrgNameFromId(serviceContractDetails.organization_id)}
            </div>
            {serviceContractDetails.resource && (
              <div className={styles.label_and_value_box}>
                <span className={styles.resource_label}>{'Resource: '}</span>
                <ResourceDrawerButton
                  labelClassName={styles.resource_name}
                  inEditState={false}
                  drawerTitle={serviceContractDetails.resource?.name}
                  label={serviceContractDetails.resource?.name}
                  resourceId={serviceContractDetails.resource_id}
                />
              </div>
            )}
            {!isNew && (
              <div className={styles.resource_docs_container}>
                <div className={styles.documents_header}>Documents</div>
                <ServiceContractDetailsDocumentsPanel
                  serviceContractDetails={serviceContractDetails}
                  onDocumentsUpdated={refreshServiceContractDetails}
                  editable
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

ServiceContractDetailsInformation.propTypes = {
  serviceContractDetailsId: PropTypes.number,
  serviceContractDetailsObject: PropTypes.object,
  serviceContractId: PropTypes.number.isRequired,
  title: PropTypes.string,
  editable: PropTypes.bool,
  parentEditState: PropTypes.bool,
  collapsible: PropTypes.bool,
  onCancel: PropTypes.func,
};

export default ServiceContractDetailsInformation;
